import { useEffect, useRef } from "react";

/**
 * useIdleLogout — WEB / MASAÜSTÜ oturum güvenliği.
 *
 * Kural:
 *  - Kullanıcının son aktivitesinden itibaren `timeoutMs` (varsayılan 2 saat)
 *    boyunca hiçbir işlem yapılmazsa `onIdle()` callback tetiklenir.
 *  - Aktivite sayılan olaylar: mousemove, mousedown, click, keydown,
 *    touchstart, scroll, wheel, pointerdown, focusin (form/input etkileşimi),
 *    input, change, visibilitychange (sekmeye geri dönüş).
 *  - Her aktivitede sayaç sıfırlanır.
 *  - Aktivite zaman damgası `localStorage`'e yazılır → aynı tarayıcıda
 *    açık başka sekme aktivite yaparsa bu sekmenin sayacı da yeniden başlar.
 *  - MOBİL kullanıcı olarak tespit edilirse hook devreye ALINMAZ (`enabled=false`
 *    caller tarafından geçilir); ayrıca `navigator.userAgent` mobil regex
 *    ile eşleşirse hook kendi kendine sessizce devre dışı kalır (double-guard).
 *  - Şifre/token bilgisi kaydedilmez — SADECE `lastActivity` epoch ms yazılır.
 *
 * Parametreler:
 *   enabled     boolean — false ise hiçbir listener bağlanmaz (opt-in).
 *   timeoutMs   number  — hareketsizlik eşiği (default: 2 * 60 * 60 * 1000).
 *   onIdle      () => void — süre dolduğunda 1 kez çağrılır.
 *   storageKey  string  — localStorage anahtarı (default: "operix:lastActivity").
 */
const MOBILE_UA_RE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "click",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
  "pointerdown",
  "focusin",
  "input",
  "change",
];

export function isMobileUserAgent() {
  if (typeof navigator === "undefined") return false;
  return MOBILE_UA_RE.test(navigator.userAgent || "");
}

export default function useIdleLogout({
  enabled = true,
  timeoutMs = 2 * 60 * 60 * 1000, // 2 saat
  onIdle,
  storageKey = "operix:lastActivity",
}) {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined") return undefined;
    // Double-guard: MOBİL tarayıcıda hook devreye alınmaz.
    if (isMobileUserAgent()) return undefined;

    let firedOnce = false;
    let checkTimer = null;

    const readLast = () => {
      try {
        const v = window.localStorage.getItem(storageKey);
        const n = v ? parseInt(v, 10) : NaN;
        return Number.isFinite(n) ? n : 0;
      } catch { return 0; }
    };
    const writeLast = (ts) => {
      try { window.localStorage.setItem(storageKey, String(ts)); } catch { /* ignore */ }
    };

    // İlk çağrıda mevcut aktivite yoksa `now` yaz — böylece taze giriş anı
    // baz alınır (bir önceki oturumdan kalma eski ts logout tetiklemesin).
    const now = Date.now();
    const last = readLast();
    if (!last || now - last > timeoutMs) {
      writeLast(now);
    }

    // Throttle: her event her defasında localStorage yazmasın — 15 sn içinde
    // en fazla 1 yazma (performans + tab-storm koruması).
    let lastWriteAt = 0;
    const THROTTLE_MS = 15 * 1000;

    const bump = () => {
      if (firedOnce) return;
      const t = Date.now();
      if (t - lastWriteAt >= THROTTLE_MS) {
        lastWriteAt = t;
        writeLast(t);
      }
    };

    // Aktivite listener'ları — passive, capture:true (form içindeki input event'leri
    // yakalanabilsin diye) ama passive olduğu için scroll performansı bozulmaz.
    const opts = { passive: true, capture: true };
    ACTIVITY_EVENTS.forEach((ev) => {
      window.addEventListener(ev, bump, opts);
    });

    // Diğer sekmelerde aktivite olursa storage event ile öğrenilir; bu sekmede
    // ekstra bir şey yapmaya gerek yok — check tick'inde localStorage okunacak.
    const onStorage = (e) => {
      if (e.key === storageKey) {
        // no-op; readLast tick içinde tekrar okunur
      }
    };
    window.addEventListener("storage", onStorage);

    // Sekmeye geri dönüş / visible olma da aktivite sayılır.
    const onVisibility = () => {
      if (document.visibilityState === "visible") bump();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Sayaç: her 30 sn kontrol — hareketsizlik süresi eşiği aştıysa onIdle çağır.
    const CHECK_INTERVAL_MS = 30 * 1000;
    checkTimer = window.setInterval(() => {
      if (firedOnce) return;
      const l = readLast();
      if (!l) return;
      if (Date.now() - l >= timeoutMs) {
        firedOnce = true;
        try { onIdleRef.current?.(); } catch { /* ignore */ }
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => {
        window.removeEventListener(ev, bump, opts);
      });
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      if (checkTimer) window.clearInterval(checkTimer);
    };
  }, [enabled, timeoutMs, storageKey]);
}

/**
 * clearIdleActivityTracking — Manuel logout / login sırasında lastActivity
 * anahtarını temizler. Böylece bir sonraki oturumda eski TS logout tetiklemez.
 */
export function clearIdleActivityTracking(storageKey = "operix:lastActivity") {
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(storageKey);
  } catch { /* ignore */ }
}
